import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getPackages, getExtras } from '../data/pricingData';
import SEO from '../components/SEO';
import './Prijzen.css';

const Prijzen = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);

  const packages = getPackages(t);
  const extras = getExtras(t);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const formatAmount = (value) => value.toLocaleString('nl-BE');
  const formatCurrency = (value) => new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);

  return (
    <div className="prijzen">
      <SEO 
        title={t('prijzen.seo_title', 'Prijzen & Pakketten')}
        description={t('prijzen.seo_desc', 'Transparante prijzen voor webdesign en development. Kies het pakket dat bij uw bedrijf past: Starter, Business of Premium.')}
        canonical="/prijzen"
      />
      {/* Hero Section */}
      <section className="prijzen-hero">
        <div className="container">
          <div className="hero-grid">
            <motion.div 
              className="hero-content"
              initial="hidden"
              animate="visible"
              variants={headerVariants}
            >
              <h1 className="page-title">
                {t('prijzen.hero_title_1', 'Investering in')} <br />
                <span className="text-gradient">{t('prijzen.hero_title_2', 'Jouw Groei')}</span>
              </h1>
              <p className="page-subtitle">
                {t('prijzen.hero_subtitle', 'Transparante prijzen, geen verrassingen. Kies het pakket dat past bij jouw ambities en budget. Wij bouwen de fundering voor jouw online succes.')}
              </p>
              <div className="hero-buttons">
                <Link to="/contact" className="btn btn-primary">
                  {t('prijzen.cta_quote', 'Vraag Offerte Aan')}
                </Link>
                <button onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })} className="btn btn-secondary">
                  {t('prijzen.cta_packages', 'Bekijk Pakketten')}
                </button>
              </div>
            </motion.div>

            <motion.div 
              className="hero-visual"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
               <svg viewBox="0 0 600 500" className="hero-svg">
                <defs>
                  <linearGradient id="growthGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#f97316" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
                  </linearGradient>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#000000" floodOpacity="0.1" />
                  </filter>
                </defs>

                {/* Background Elements */}
                <circle cx="300" cy="250" r="200" fill="#fff7ed" opacity="0.5" />
                <circle cx="300" cy="250" r="150" fill="#ffedd5" opacity="0.3" />

                {/* Chart Card */}
                <motion.g
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <rect x="100" y="150" width="400" height="250" rx="16" fill="white" filter="url(#shadow)" />
                  
                  {/* Chart Grid */}
                  <line x1="140" y1="350" x2="460" y2="350" stroke="#e2e8f0" strokeWidth="2" />
                  <line x1="140" y1="300" x2="460" y2="300" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" />
                  <line x1="140" y1="250" x2="460" y2="250" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" />
                  <line x1="140" y1="200" x2="460" y2="200" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" />

                  {/* Growth Line */}
                  <motion.path 
                    d="M140 350 C 200 350, 250 300, 300 280 S 400 250, 460 180" 
                    fill="none" 
                    stroke="#f97316" 
                    strokeWidth="4" 
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
                  />
                  
                  {/* Area under curve */}
                  <motion.path 
                    d="M140 350 C 200 350, 250 300, 300 280 S 400 250, 460 180 V 350 H 140 Z" 
                    fill="url(#growthGrad)" 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1 }}
                  />

                  {/* Data Points */}
                  <motion.circle cx="140" cy="350" r="6" fill="white" stroke="#f97316" strokeWidth="3" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 }} />
                  <motion.circle cx="300" cy="280" r="6" fill="white" stroke="#f97316" strokeWidth="3" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.0 }} />
                  <motion.circle cx="460" cy="180" r="8" fill="#f97316" stroke="white" strokeWidth="3" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.5 }} />
                </motion.g>

                {/* Floating Elements */}
                {/* Price Tag */}
                <motion.g
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 1.2, type: "spring" }}
                >
                  <rect x="50" y="120" width="120" height="60" rx="12" fill="white" filter="url(#shadow)" />
                  <circle cx="80" cy="150" r="15" fill="#10b981" opacity="0.2" />
                  <path d="M72 150 L78 156 L88 144" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <rect x="105" y="140" width="40" height="8" rx="4" fill="#e2e8f0" />
                  <rect x="105" y="155" width="30" height="6" rx="3" fill="#e2e8f0" />
                </motion.g>

                {/* Rocket Icon */}
                <motion.g
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 1.8, type: "spring" }}
                >
                  <circle cx="500" cy="120" r="40" fill="white" filter="url(#shadow)" />
                  <path d="M500 100 L500 140 M480 120 L520 120" stroke="none" /> {/* Placeholder for positioning */}
                  {/* Simple Rocket Shape */}
                  <path d="M490 130 L500 110 L510 130 L500 125 Z" fill="#f97316" transform="translate(0, -5)" />
                  <path d="M500 125 L500 135" stroke="#f97316" strokeWidth="2" />
                </motion.g>

              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section id="pricing" className="pricing-section section">
        <div className="container">
          <motion.div 
            className="pricing-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
          >
            {packages.map((pkg, index) => (
              <motion.div 
                key={index} 
                className={`pricing-card card ${pkg.popular ? 'popular' : ''}`}
                variants={cardVariants}
              >
                
                <div className="pricing-header">
                  <span className="package-badge">{pkg.badge}</span>
                  <h3 className="package-name">{pkg.name}</h3>
                  <div className="package-price-wrapper">
                    <div className="package-price old-price">
                      <span className="currency">€</span>
                      <span className="amount">{formatAmount(pkg.oldPrice)}</span>
                    </div>
                    <div className="package-price new-price">
                      <span className="currency">€</span>
                      <span className="amount">{formatAmount(pkg.newPrice)}</span>
                    </div>
                  </div>
                  <p className="price-note">{t('prijzen.excl_vate', 'excl. btw')}</p>
                </div>

                <div className="pricing-body">
                  <p className="package-description">{pkg.description}</p>
                  
                  <div className="ideal-for">
                    <strong>{t('prijzen.ideal_for', 'Ideaal voor:')}</strong> {pkg.ideal}
                  </div>

                  <div className="features-section">
                    <h4>{t('prijzen.included', 'Inbegrepen:')}</h4>
                    <ul className="features-list">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx}>
                          <span className="check-icon"><Check size={14} strokeWidth={3} /></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="maintenance-section">
                  <h4>{t('prijzen.maintenance_option', 'Onderhoudsoptie:')} €{formatAmount(pkg.maintenance.price)}/{t('prijzen.month', 'maand')}</h4>
                    <ul className="maintenance-list">
                      {pkg.maintenance.features.map((feature, idx) => (
                        <li key={idx}>
                          <span className="bullet">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pricing-footer">
                  <Link 
                    to="/contact" 
                    className={`btn ${pkg.popular ? 'btn-primary' : 'btn-secondary'} btn-full`}
                  >
                    {t('prijzen.choose', 'Kies')} {pkg.name}
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Extras Section */}
      <section className="extras-section section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">{t('prijzen.extras_title', "Uitbreidingen & Extra's")}</h2>
            <p className="section-subtitle">{t('prijzen.extras_subtitle', 'Maak uw website compleet met deze krachtige add-ons.')}</p>
          </div>

          <div className="extras-grid">
            {extras.map((category, catIndex) => (
              <motion.div 
                key={catIndex} 
                className="extras-category"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: catIndex * 0.1 }}
              >
                <h3 className="category-title">{category.category}</h3>
                <div className="category-items">
                  {category.items.map((item, itemIndex) => {
                    const priceLabel = item.isVariable ? `${t('prijzen.from', 'vanaf')} ${formatCurrency(item.price)}` : formatCurrency(item.price);
                    return (
                      <div key={itemIndex} className="extra-card">
                        <div className="extra-icon-wrapper">
                          {item.icon}
                        </div>
                        <div className="extra-content">
                          <div className="extra-header">
                            <h4>{item.title}</h4>
                            {item.tag && <span className="extra-tag">{item.tag}</span>}
                          </div>
                          <p className="extra-description">{item.description}</p>
                          <div className="extra-price">
                            <span className="amount">{priceLabel}</span>
                            {item.unit && <span className="unit"> {item.unit}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="comparison-section section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">{t('prijzen.compare_title', 'Vergelijk de Details')}</h2>
            <p className="section-subtitle">{t('prijzen.compare_subtitle', 'Een gedetailleerd overzicht van alle functionaliteiten per pakket.')}</p>
          </div>
          
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th className="feature-header">{t('prijzen.table.features', 'Kenmerken')}</th>
                  {packages.map((pkg, index) => (
                    <th key={index} className={`package-header ${pkg.popular ? 'popular' : ''}`}>
                      <div className="header-card">
                        <h4 className="header-package-name">{pkg.name}</h4>
                        <div className="header-pricing">
                          <span className="header-old-price">€{formatAmount(pkg.oldPrice)}</span>
                          <span className="header-new-price">€{formatAmount(pkg.newPrice)}</span>
                        </div>
                        <Link to="/contact" className="header-cta-btn">
                          {t('prijzen.table.cta', 'Kies dit pakket')}
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="section-header"><td colSpan="4">{t('prijzen.table.section_design', 'Design & Content')}</td></tr>
                <tr>
                  <td>{t('prijzen.table.num_pages', "Aantal Pagina's")}</td>
                  <td>{t('prijzen.table.1_page', '1 (Onepager)')}</td>
                  <td className="highlight-col">{t('prijzen.table.up_to_5', 'Tot 5')}</td>
                  <td>{t('prijzen.table.up_to_8', 'Tot 8')}</td>
                </tr>
                <tr>
                  <td>{t('prijzen.table.design_custom', 'Design Maatwerk')}</td>
                  <td>{t('prijzen.table.basic', 'Basis')}</td>
                  <td className="highlight-col">{t('prijzen.table.extensive', 'Uitgebreid')}</td>
                  <td>{t('prijzen.table.premium_anim', 'Premium + Animaties')}</td>
                </tr>
                <tr>
                  <td>{t('prijzen.table.responsive', 'Responsive (Mobiel)')}</td>
                  <td><Check size={20} className="check-icon-green" /></td>
                  <td className="highlight-col"><Check size={20} className="check-icon-green" /></td>
                  <td><Check size={20} className="check-icon-green" /></td>
                </tr>

                <tr className="section-header"><td colSpan="4">{t('prijzen.table.section_tech', 'Techniek & SEO')}</td></tr>
                <tr>
                  <td>{t('prijzen.table.cms', 'CMS (Zelf beheren)')}</td>
                  <td><span className="not-included">—</span></td>
                  <td className="highlight-col"><span className="not-included">—</span></td>
                  <td><Check size={20} className="check-icon-green" /> <span className="feature-note">(Strapi)</span></td>
                </tr>
                <tr>
                  <td>{t('prijzen.table.seo', 'SEO Optimalisatie')}</td>
                  <td>{t('prijzen.table.basic', 'Basis')}</td>
                  <td className="highlight-col">{t('prijzen.table.extensive', 'Uitgebreid')}</td>
                  <td>{t('prijzen.table.advanced', 'Geavanceerd')}</td>
                </tr>
                <tr>
                  <td>Google Analytics</td>
                  <td><span className="not-included">—</span></td>
                  <td className="highlight-col"><Check size={20} className="check-icon-green" /></td>
                  <td><Check size={20} className="check-icon-green" /></td>
                </tr>
                <tr>
                  <td>{t('prijzen.table.speed', 'Snelheid')}</td>
                  <td>{t('prijzen.table.fast', 'Snel')}</td>
                  <td className="highlight-col">{t('prijzen.table.very_fast', 'Zeer Snel')}</td>
                  <td>{t('prijzen.table.super_fast', 'Bliksemsnel (Next.js)')}</td>
                </tr>

                <tr className="section-header"><td colSpan="4">{t('prijzen.table.section_service', 'Service')}</td></tr>
                <tr>
                  <td>{t('prijzen.table.delivery', 'Oplevertijd')}</td>
                  <td>{t('prijzen.table.1_week', '1 week')}</td>
                  <td className="highlight-col">{t('prijzen.table.1_5_2_weeks', '1.5 - 2 weken')}</td>
                  <td>{t('prijzen.table.3_4_weeks', '3 - 4 weken')}</td>
                </tr>
                <tr>
                  <td>{t('prijzen.table.revisions', 'Revisierondes')}</td>
                  <td>1</td>
                  <td className="highlight-col">2</td>
                  <td>{t('prijzen.table.unlimited', 'Onbeperkt')}</td>
                </tr>
                <tr>
                  <td>{t('prijzen.table.support', 'Support')}</td>
                  <td>{t('prijzen.table.email', 'E-mail')}</td>
                  <td className="highlight-col">{t('prijzen.table.email_phone', 'E-mail & Tel')}</td>
                  <td>{t('prijzen.table.priority', 'Priority Support')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="section-title">{t('prijzen.faq_title', 'Veelgestelde Vragen')}</h2>
            <p className="section-subtitle">
              {t('prijzen.faq_subtitle', 'Alles wat u moet weten over onze prijzen en diensten')}
            </p>
          </motion.div>

          <div className="faq-container">
            <div className={`faq-item ${activeIndex === 0 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(0)}>
                <span>{t('prijzen.faq.0.q', 'Wat kost een nieuwe website?')}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 0 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 0 ? 'auto' : 0,
                  opacity: activeIndex === 0 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.0.a', 'De kosten zijn afhankelijk van de complexiteit en functionaliteiten van de website. Een Starter pakket begint vanaf €550, Business vanaf €1.450, en Premium vanaf €2.850. Alle prijzen zijn exclusief BTW. Neem contact met ons op voor een persoonlijke offerte op maat.')}</p>
              </motion.div>
            </div>

            <div className={`faq-item ${activeIndex === 1 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(1)}>
                <span>{t('prijzen.faq.1.q', 'Wanneer is mijn website klaar?')}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 1 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 1 ? 'auto' : 0,
                  opacity: activeIndex === 1 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.1.a', 'Starter pakket: binnen 1 week. Business pakket: 1,5 à 2 weken. Premium pakket: 3 à 4 weken. We houden je gedurende het hele proces op de hoogte van de voortgang.')}</p>
              </motion.div>
            </div>

            <div className={`faq-item ${activeIndex === 2 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(2)}>
                <span>{t('prijzen.faq.2.q', 'Kom ik voor verrassingen te staan?')}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 2 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 2 ? 'auto' : 0,
                  opacity: activeIndex === 2 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.2.a', 'Nee, alle kosten zijn transparant. De genoemde prijzen zijn exclusief BTW. Hosting en basis onderhoud zijn inbegrepen in de onderhoudsopties. Er zijn geen verrassingen.')}</p>
              </motion.div>
            </div>

            <div className={`faq-item ${activeIndex === 3 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(3)}>
                <span>{t('prijzen.faq.3.q', 'Sta ik er alleen voor na de lancering?')}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 3 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 3 ? 'auto' : 0,
                  opacity: activeIndex === 3 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.3.a', 'U krijgt volledige controle over uw website inclusief alle broncode en toegangsgegevens. Optioneel kunt u kiezen voor een onderhoudscontract voor zorgeloos websitebeheer.')}</p>
              </motion.div>
            </div>

            <div className={`faq-item ${activeIndex === 4 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(4)}>
                <span>{t('prijzen.faq.4.q', 'Kan ik later nog uitbreiden?')}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 4 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 4 ? 'auto' : 0,
                  opacity: activeIndex === 4 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.4.a', 'Ja, absoluut! U kunt altijd upgraden naar een uitgebreider pakket. We rekenen dan enkel het verschil en zorgen voor een soepele transitie.')}</p>
              </motion.div>
            </div>

            <div className={`faq-item ${activeIndex === 5 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(5)}>
                <span>{t('prijzen.faq.5.q', 'Moet ik een onderhoudscontract nemen?')}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 5 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 5 ? 'auto' : 0,
                  opacity: activeIndex === 5 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.5.a', 'Nee, de onderhoudsoptie is volledig optioneel. U kunt er ook later voor kiezen of het beheer zelf doen. We raden het wel aan voor optimale prestaties en beveiliging.')}</p>
              </motion.div>
            </div>

            <div className={`faq-item ${activeIndex === 6 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(6)}>
                <span>{t('prijzen.faq.6.q', 'Wat zijn de betalingsvoorwaarden?')}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 6 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 6 ? 'auto' : 0,
                  opacity: activeIndex === 6 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.6.a', 'We hanteren een betaling in 2 termijnen: 50% bij start en 50% bij oplevering. Voor Premium projecten is een betalingsplan mogelijk om het voor u betaalbaarder te maken.')}</p>
              </motion.div>
            </div>

            <div className={`faq-item ${activeIndex === 7 ? 'active' : ''}`}>
              <button className="faq-question" onClick={() => toggleAccordion(7)}>
                <span>{t('prijzen.faq.7.q', "Kan ik meer pagina's toevoegen?")}</span>
                <motion.span 
                  className="faq-icon"
                  animate={{ rotate: activeIndex === 7 ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.span>
              </button>
              <motion.div 
                className="faq-answer"
                initial={false}
                animate={{
                  height: activeIndex === 7 ? 'auto' : 0,
                  opacity: activeIndex === 7 ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <p>{t('prijzen.faq.7.a', "Extra pagina's kunnen altijd worden toegevoegd tegen een aantrekkelijk tarief. We maken graag een offerte op maat voor uw specifieke behoeften en wensen.")}</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="prijzen-cta section">
        <div className="container">
          <motion.div 
            className="cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="cta-bg-pattern">
              <div className="cta-circle cta-circle-1"></div>
              <div className="cta-circle cta-circle-2"></div>
            </div>
            
            <div className="cta-content">
              <h2>{t('prijzen.cta_footer_title', 'Klaar om Digitaal te Versnellen?')}</h2>
              <p>
                {t('prijzen.cta_footer_text', 'Heeft u een concreet idee of zoekt u nog naar de juiste richting? Wij denken graag met u mee. Plan een vrijblijvende kennismaking en ontdek wat wij voor uw bedrijf kunnen betekenen.')}
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  {t('prijzen.cta_footer_btn_primary', 'Start uw Project')}
                </Link>
                <Link to="/portfolio" className="btn btn-secondary">
                  {t('prijzen.cta_footer_btn_secondary', 'Bekijk Portfolio')}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Prijzen;
